
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Repeat, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNodeSettings } from '../NodeSettingsContext';
import { type StateNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface MapNodeProps {
    data: StateNodeData;
    selected?: boolean;
}

export const MapNode = memo(function MapNode({ data, selected }: MapNodeProps) {
    const { openSettings } = useNodeSettings();
    const { label, state, layoutDirection = 'LR' } = data;

    // Determine handle positions based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;

    return (
        <div
            className={cn(
                'relative min-h-[100px] min-w-[260px] rounded-lg border-2 border-emerald-300 bg-emerald-50/50',
                'shadow-md',
                selected && 'ring-2 ring-blue-500 ring-offset-2'
            )}
        >
            {/* Input Handle - position based on layout direction */}
            <Handle
                type="target"
                id="target"
                position={inputPosition}
                className="!h-3 !w-3 !border-emerald-500 !bg-emerald-400"
            />

            {/* Header */}
            <div className="flex items-center gap-2 rounded-t-lg border-b border-emerald-200 bg-emerald-100 px-3 py-2">
                <Repeat className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-medium text-slate-900">{label}</span>
                {state.MaxConcurrency && (
                    <span className="ml-auto rounded-full bg-emerald-200 px-2 py-0.5 text-xs text-emerald-700">
                        max {state.MaxConcurrency}
                    </span>
                )}
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded-full text-slate-400 hover:bg-emerald-200 hover:text-slate-600 ml-auto"
                    title="Node Settings"
                    onClick={(e) => {
                        e.stopPropagation();
                        openSettings(data);
                    }}
                >
                    <Settings size={12} />
                </Button>
            </div>

            {/* Content */}
            <div className="space-y-1 p-3 text-xs text-slate-500">
                {state.ItemsPath && (
                    <p>
                        <span className="text-slate-400">Items:</span>{' '}
                        <code className="rounded bg-emerald-100 px-1 font-mono">{state.ItemsPath}</code>
                    </p>
                )}
                {state.Comment && <p className="italic">{state.Comment}</p>}
            </div>

            {/* Output Handle - position based on layout direction */}
            <Handle
                type="source"
                id="source"
                position={outputPosition}
                className="!h-3 !w-3 !border-emerald-500 !bg-emerald-400"
            />
        </div>
    );
});
